/**
 * Polyfills runtime methods that some browsers still in our traffic predate: the ES2023
 * change-array-by-copy methods (Chrome <110, Safari <16.4, Firefox <115) and ES2022's
 * `Object.hasOwn` (Chrome <93, Safari <15.4, Firefox <92). These are runtime methods rather than
 * syntax, so no build target transpiles them away.
 *
 * Must be imported before any application code so the methods exist by the time it runs.
 */

type ArrayMethodName = 'toReversed' | 'toSorted' | 'toSpliced' | 'with'

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const definePolyfill = (name: ArrayMethodName, implementation: (this: any[], ...args: any[]) => unknown) => {
  if (typeof Array.prototype[name] === 'function') return

  Object.defineProperty(Array.prototype, name, {
    value: implementation,
    writable: true,
    configurable: true,
    enumerable: false
  })
}

definePolyfill('toReversed', function () {
  return [...this].reverse()
})

definePolyfill('toSorted', function (compareFn) {
  if (compareFn !== undefined && typeof compareFn !== 'function') {
    throw new TypeError('The comparison function must be either a function or undefined')
  }
  return [...this].sort(compareFn)
})

// Forwarded as a spread so an omitted skipCount keeps splice's "remove through the end"
// behaviour instead of being coerced to 0.
definePolyfill('toSpliced', function (...args) {
  const copy = [...this]
  copy.splice(...(args as [number, number?]))
  return copy
})

definePolyfill('with', function (index, value) {
  const copy = [...this]
  const actualIndex = index < 0 ? copy.length + index : index
  if (actualIndex < 0 || actualIndex >= copy.length) {
    throw new RangeError(`Invalid index : ${index}`)
  }
  copy[actualIndex] = value
  return copy
})

if (typeof Object.hasOwn !== 'function') {
  Object.defineProperty(Object, 'hasOwn', {
    value: function hasOwn(object: unknown, property: PropertyKey) {
      if (object === null || object === undefined) {
        throw new TypeError('Cannot convert undefined or null to object')
      }
      return Object.prototype.hasOwnProperty.call(Object(object), property)
    },
    writable: true,
    configurable: true,
    enumerable: false
  })
}

export {}
